import React, { useCallback, useMemo, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import Dataset from "datasets/types/interfaces/dataset";
import { AlertCircle, MessageSquareShare, UserRoundPlus } from "lucide-react";
import useSWR from "swr";
import { Button } from "~/components/ui/button";
import useNextGenHeader from "~/contexts/nextgen-header";
import useUser from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import { ShareModal } from "~/pages/manifold-nextgen/components/share/ShareModal";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import { nextgenProjectPath, nextgenProjectsListPath } from "~/pages/manifold-nextgen/paths";
import { Agent, agentPath } from "~/services/agents";
import {
  Project,
  ProjectConversation,
  ShareLevel,
  projectAgentsPath,
  projectConversationsPath,
  projectDatasetsPath,
  projectPath,
} from "~/services/projects";
import { apiClient } from "~/utils/apiClient";
import { ProjectSidebar } from "./ProjectSidebar";
import { ShareConversationDialog } from "./ShareConversationDialog";

const { VITE_CATALOG_API } = import.meta.env;
const CATALOG_DATASETS_URL = `${VITE_CATALOG_API}/datasets?offset=0&limit=1000&sort_by=title&sort_order=asc`;

export interface ProjectOutletContext {
  projectId: string;
  project: Project;
  // Whether the caller owns (can_manage) this project. Gates owner-only controls.
  isOwner: boolean;
  sessions: ProjectConversation[] | undefined;
  attachedAgents: Agent[] | undefined;
  attachedAgentsWithDetails: Agent[] | undefined;
  attachedDatasets: Dataset[] | undefined;
}

export const ProjectLayout = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const currentUser = useUser();
  const [shareOpen, setShareOpen] = useState(false);
  const [projectShareOpen, setProjectShareOpen] = useState(false);

  const {
    collaborators,
    isLoading: collaboratorsLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel,
  } = useProjectShare(projectId ?? "");

  const {
    data: project,
    error,
    isLoading,
  } = useFetcher<Project>(projectId ? projectPath(projectId) : null);

  const { data: attachedAgents } = useFetcher<Agent[]>(
    projectId ? projectAgentsPath(projectId) : null
  );

  const agentIds = attachedAgents?.map((a) => a.id);
  // Fetches full agent details (including starter_prompts) individually because
  // GET /projects/:id/agents returns a lightweight shape without that field.
  // Ideally starter_prompts would be included in that endpoint to avoid N requests.
  const { data: agentsWithDetails } = useSWR<Agent[]>(
    agentIds?.length ? ["project-agents-full", ...[...agentIds].sort()] : null,
    ([, ...ids]: [string, ...string[]]) =>
      Promise.all(ids.map((id) => apiClient<Agent>(agentPath(id))))
  );

  const { data: attachedDatasetIds } = useFetcher<string[]>(
    projectId ? projectDatasetsPath(projectId) : null
  );

  const { data: catalogDatasets } = useFetcher<Dataset[]>(CATALOG_DATASETS_URL);

  const { data: sessionsList } = useFetcher<{ items: ProjectConversation[] }>(
    projectId ? projectConversationsPath(projectId) : null
  );
  const sessions = sessionsList?.items;

  const attachedDatasets = useMemo<Dataset[] | undefined>(() => {
    if (attachedDatasetIds === undefined || catalogDatasets === undefined) {
      return undefined;
    }
    const byId = new Map(catalogDatasets.map((d) => [d.id, d]));
    return attachedDatasetIds.map((id) => byId.get(id)).filter((d): d is Dataset => Boolean(d));
  }, [attachedDatasetIds, catalogDatasets]);

  const conversationId = location.pathname.match(/\/c\/([^/]+)/)?.[1];
  const isSettings =
    location.pathname.endsWith("/settings") || location.pathname.endsWith("/settings-v2");
  const currentSession = conversationId
    ? sessions?.find((s) => s.id === conversationId)
    : undefined;

  const breadcrumbs = useMemo(() => {
    const crumbs: { label: string; href?: string }[] = [
      { label: "All Projects", href: nextgenProjectsListPath() },
      {
        label: project?.name ?? "Loading…",
        href:
          (conversationId || isSettings) && projectId ? nextgenProjectPath(projectId) : undefined,
      },
    ];

    if (conversationId) {
      crumbs.push({ label: currentSession?.display_name ?? "Loading…" });
    } else if (isSettings) {
      crumbs.push({ label: "Settings" });
    }

    return crumbs;
  }, [project?.name, projectId, conversationId, isSettings, currentSession?.display_name]);

  const handleOpenShare = useCallback(() => setShareOpen(true), []);
  const handleOpenProjectShare = useCallback(() => setProjectShareOpen(true), []);

  // Only surface a share action once the project has loaded: the modals it opens
  // are rendered in the success-path return below, so a button shown during load
  // or on the error state would click into nothing.
  const headerActions = useMemo<[React.ReactNode?, React.ReactNode?]>(() => {
    if (!project) {
      return [];
    }
    if (conversationId) {
      // Anyone can share their own session, owner or not.
      return [
        <Button
          key="share-conversation"
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={handleOpenShare}
        >
          <MessageSquareShare className="size-4" />
          Share Session
        </Button>,
      ];
    }
    // Only owners can grant access to the project itself.
    if (project.access_level !== ShareLevel.OWNER) {
      return [];
    }
    return [
      <Button
        key="share-project"
        variant="outline"
        size="sm"
        className="text-xs"
        onClick={handleOpenProjectShare}
      >
        <UserRoundPlus className="size-4" />
        Share Project
      </Button>,
    ];
  }, [project, conversationId, handleOpenShare, handleOpenProjectShare]);

  useNextGenHeader({ breadcrumbs, actions: headerActions });

  if (!projectId) {
    return (
      <div role="alert" className="p-6 text-sm text-destructive">
        Missing project id.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full overflow-hidden">
        <div
          className="w-56 shrink-0 border-r border-border"
          role="status"
          aria-label="Loading project"
        />
        <div className="flex-1" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div
        role="alert"
        className="flex h-full flex-col items-center justify-center gap-3 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load project</p>
      </div>
    );
  }

  const outletContext: ProjectOutletContext = {
    projectId,
    project,
    isOwner: project.access_level === ShareLevel.OWNER,
    sessions,
    attachedAgents,
    attachedAgentsWithDetails: agentsWithDetails,
    attachedDatasets,
  };

  return (
    <>
      <div className="flex h-full overflow-hidden">
        <ProjectSidebar projectId={projectId} sessions={sessions} />
        <div className="flex flex-1 flex-col overflow-hidden">
          <Outlet context={outletContext} />
        </div>
      </div>
      {conversationId && (
        <ShareConversationDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          conversationId={conversationId}
        />
      )}
      <ShareModal
        open={projectShareOpen}
        onOpenChange={setProjectShareOpen}
        resourceLabel="project"
        resourceName={project.name}
        collaborators={collaborators}
        collaboratorsLoading={collaboratorsLoading}
        collaboratorsError={collaboratorsError}
        accessLevels={accessLevels}
        defaultAccessLevel={defaultAccessLevel}
        onShare={handleShare}
        currentUserEmail={currentUser?.email}
      />
    </>
  );
};
